import React, { useContext, useEffect, useMemo, useState } from 'react'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Alert, Badge, Box, Chip, Collapse, IconButton, Typography, useTheme } from '@mui/material'

import indicatorConditionString from '../../../common/utils/getConditionLabel'
import { ConditionGroupType, ConditionType } from '../contexts/PatternTreeContext'
import { PreviewConflictContext } from './PreviewConflictContext'
import PreviewChart from './render/PreviewChart'
import { usePreviewResult } from './usePreviewResult'

const COLLAPSED_STORAGE_KEY = 'gc.preview.collapsed'

const findCondition = (
  groups: ConditionGroupType[] | undefined,
  conditionId: string
): ConditionType | undefined => {
  for (const group of groups ?? []) {
    const direct = (group.conditions ?? []).find((child) => child.id === conditionId)
    if (direct) return direct
    const nested = findCondition(group.conditionGroups, conditionId)
    if (nested) return nested
  }
  return undefined
}

/**
 * Live snapshot of what the active side's conditions describe: one mini-chart
 * per distinct symbol/periodicity/timeframe, gray candles unless a condition
 * entails their direction, and warn-only conflict reporting. Read-only over
 * PatternTreeContext state — never blocks saving.
 */
const PatternPreviewPanel = () => {
  const theme = useTheme()
  const { conflictConditionIds } = useContext(PreviewConflictContext)
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true'
  )
  const preview = usePreviewResult(!collapsed)
  const result = preview?.result ?? null

  const toggleCollapsed = () => setCollapsed((previous) => !previous)
  // Persist outside the state updater — StrictMode invokes updaters twice.
  useEffect(() => {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed))
  }, [collapsed])

  // Keyed on the SOLVED-FROM groups snapshot, not the live context groups
  // (whose reference changes per keystroke) — so this holds between solves
  // and React.memo on PreviewChart stays effective.
  const solvedGroups = preview?.solvedGroups
  const conditionLabels: Record<string, string> = useMemo(() => {
    const labels: Record<string, string> = {}
    if (!result) return labels
    const labelFor = (conditionId: string): string => {
      const condition = findCondition(solvedGroups, conditionId)
      return condition ? indicatorConditionString(condition) : conditionId
    }
    const wanted = new Set<string>([...result.conflictConditionIds, ...result.droppedConditionIds])
    result.charts.forEach((chart) => {
      chart.badges.forEach((badge) => wanted.add(badge.conditionId))
    })
    wanted.forEach((conditionId) => {
      labels[conditionId] = labelFor(conditionId)
    })
    return labels
  }, [result, solvedGroups])

  const labelOf = (conditionId: string): string => conditionLabels[conditionId] ?? conditionId

  // Context ids are the single source for conflict indicators: they clear on
  // collapse, so the header badge and the card outlines always agree.
  const conflictCount = conflictConditionIds.length

  return (
    <Box
      data-tour-id="pattern-preview"
      sx={{
        flexShrink: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.custom2,
      }}
    >
      <Box
        onClick={toggleCollapsed}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.75,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Typography variant="subtitle1">Preview</Typography>
        {conflictCount > 0 && (
          <Badge badgeContent={conflictCount} color="warning" sx={{ ml: 1.5 }} />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" aria-label={collapsed ? 'expand preview' : 'collapse preview'}>
          {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Box>
      <Collapse in={!collapsed}>
        <Box sx={{ px: 2, pb: 1.5, maxHeight: '40vh', overflowY: 'auto' }}>
          {!result || result.charts.length === 0 ? (
            result && result.status !== 'empty' ? null : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Add conditions to see a preview.
              </Typography>
            )
          ) : (
            result.charts.map((chart) => (
              <Box key={chart.chartKey} sx={{ mb: 1 }}>
                {result.charts.length > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    {chart.chartKey.replace(/_/g, ' · ')}
                  </Typography>
                )}
                <PreviewChart chart={chart} conditionLabels={conditionLabels} />
              </Box>
            ))
          )}
          {result?.status === 'conflict' && (
            <Alert severity="warning" sx={{ mb: 1 }} data-testid="preview-conflict-alert">
              These conditions can’t all be true at once:
              {result.conflictConditionIds.map((conditionId) => (
                <Box key={conditionId} component="span" sx={{ display: 'block' }}>
                  • {labelOf(conditionId)}
                </Box>
              ))}
            </Alert>
          )}
          {result?.status === 'unverified' && (
            <Typography variant="caption" color="text.secondary">
              Couldn’t fully verify this pattern — showing best effort.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {result?.droppedConditionIds.map((conditionId) => (
              <Chip
                key={conditionId}
                size="small"
                variant="outlined"
                label={`or: ${labelOf(conditionId)}`}
              />
            ))}
            {result?.warnings.map((warning, i) => (
              <Chip
                key={`${warning.kind}-${i}`}
                size="small"
                color="warning"
                variant="outlined"
                label={warning.message}
              />
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}

export default PatternPreviewPanel
